import { resolveRating } from "@/modules/lead-page/api/lead-page.v2.api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useCallback } from "react";
import {
  ResolveRatingParams,
  UseResolveRatingOptions,
} from "@/modules/lead-page/types";

export const useResolveRating = ({
  ratingUId,
  onSuccess,
}: UseResolveRatingOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<any>,
    { response?: { data?: { message?: string } } },
    ResolveRatingParams
  >(({ ratingUId, data }) => resolveRating(ratingUId, data), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["leadRatings"] });
      queryClient.invalidateQueries({
        queryKey: ["leadRatingDetails", variables.ratingUId],
      });
      showToast({
        type: ToastType.SUCCESS,
        message: "Rating resolved successfully",
        toastId: TOAST_IDS.RESOLVE_RATING_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      onSuccess?.();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to resolve rating";
      showToast({
        type: ToastType.ERROR,
        message: errorMessage,
        toastId: TOAST_IDS.RESOLVE_RATING_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const handleResolveRating = useCallback(
    (comment?: string) => {
      if (mutation.isPending) return;

      mutation.mutate({
        ratingUId,
        data: comment ? { comment: comment.trim() } : {},
      });
    },
    [ratingUId, mutation]
  );

  return {
    handleResolveRating,
    isPending: mutation.isPending,
  };
};
